import React from 'react';
import Spinner from '@atlaskit/spinner';
import isInteger from 'lodash/isInteger';
import { Checkbox } from '@atlaskit/checkbox';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Y200 } from '@atlaskit/theme/colors';
import Textfield from '@atlaskit/textfield';
import { Field, CheckboxField, ErrorMessage } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import { isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import { PricingPlanOption } from '@atlassian/cssxp-typescommon';
import { CustomPanel } from '../../custom-panel';
import EntitlementIcon from '../../entitlement-icon/entitlement-icon';
import {
  EntitlementDetails,
  LinkedDestinationEntitlement,
  BillingFrequency,
  Maybe,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  ManagePromotionsSection,
  PromotionFieldConfig,
  AddedPromotionsType,
} from '../../manage-promotions-section/src/manage-promotions-section';
import './manage-subscription-panel.scss';
import { PromotionItemDetail, SelectPromotionSelectItem } from '../../forms';
import { SandboxLozenge } from '../../copyable-text';

export interface OfferingOption {
  label: string;
  value: string; // offering id
  pricingType?: Maybe<string>;
  level?: Maybe<number>;
  status?: Maybe<string>;
}

export interface UserTierOption {
  label: string;
  value: Maybe<number>; // user tier value
}

export interface FieldConfig extends PromotionFieldConfig {
  skipTrial?: boolean;
  userTier?: boolean;
  offering?: boolean;
  pricingPlan?: boolean;
  units?: boolean;
}

export type OnChangeFieldValue =
  | ValueType<OfferingOption, false>
  | ValueType<UserTierOption, false>
  | ValueType<PricingPlanOption, false>
  | boolean
  | string
  | SelectPromotionSelectItem[]
  | PromotionItemDetail;

interface Props {
  fieldName?: string;
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  offeringOptions?: OfferingOption[];
  userTierOptions?: UserTierOption[];
  pricingPlanOptions?: PricingPlanOption[];
  isSkipTrialChecked?: boolean;
  selectedOffering?: OfferingOption | null;
  selectedUserTier?: UserTierOption | null;
  selectedPricingPlan?: PricingPlanOption | null;
  selectedUnits?: string;
  onChange?: (fieldName: string, data: OnChangeFieldValue) => void;
  isDisabled?: FieldConfig;
  isLoading?: FieldConfig;
  isHidden?: FieldConfig;
  isRequired?: FieldConfig;
  billingFrequency?: BillingFrequency;
  availablePromotions?: (Promotion | null)[] | null;
  partnerPromotion?: Promotion | null;
  selectedPromotionsToAdd?: AddedPromotionsType;
  selectedPromotionsToRemove?: SelectPromotionSelectItem[];
  selectedSchedulePromotionsToRemove?: SelectPromotionSelectItem[];
  hasOptedForUpgradeTrial?: boolean;
  showLoader?: boolean;
  isExpanded?: boolean;
}

export const ManageSubscriptionPanel: React.FC<Props> = ({
  fieldName,
  entitlement,
  offeringOptions,
  userTierOptions,
  pricingPlanOptions,
  isSkipTrialChecked,
  selectedOffering,
  selectedUserTier,
  selectedPricingPlan,
  selectedUnits,
  onChange,
  isDisabled,
  isHidden,
  isLoading,
  isRequired,
  billingFrequency,
  availablePromotions,
  partnerPromotion,
  selectedPromotionsToAdd,
  selectedPromotionsToRemove,
  selectedSchedulePromotionsToRemove,
  hasOptedForUpgradeTrial,
  showLoader,
  isExpanded = false,
}: Props) => {
  const isRequiredConfig = {
    units: true,
    ...isRequired,
  };
  const isHiddenConfig = {
    units: !isStatuspage(entitlement?.productId),
    userTier: isStatuspage(entitlement?.productId) || billingFrequency !== BillingFrequency.ANNUAL,
    ...isHidden,
  };

  const { logoId, productId, productName, entitlementNumber, isSandbox } = entitlement ?? {};
  const fieldNamePrefix = fieldName ? `${fieldName}.` : '';
  const header = (
    <div>
      <EntitlementIcon
        logoId={logoId as string}
        productId={productId as string}
        isCloud={true}
        className="avatar medium"
      />{' '}
      <span>{productName || 'Unnamed product'}</span>
      {isSandbox && <SandboxLozenge />}
      <h2 className="entitlement-number">{entitlementNumber}</h2>
    </div>
  );

  return (
    <div data-testid={`${fieldNamePrefix}manage-subscription-panel`} className="manage-subscription-panel-container">
      <CustomPanel isExpanded={isExpanded} expandedHeader={header} collapsedHeader={header}>
        <div className="panel-content-container">
          {showLoader && (
            <div className="spinning-loader">
              <Spinner testId="spinning-loader" size="large" />
            </div>
          )}
          <div className="panel-content">
            <div className="flex-row">
              {isHiddenConfig?.offering || (
                <div className="field-width">
                  <Field<ValueType<OfferingOption>, HTMLSelectElement>
                    name={`${fieldNamePrefix}offering`}
                    id={`${fieldNamePrefix}offering`}
                    label="Plan"
                    defaultValue={selectedOffering}
                    isDisabled={!!isDisabled?.offering}
                    {...(isRequiredConfig?.offering && {
                      isRequired: true,
                      validate: (value: any) => (!value ? 'EMPTY' : undefined),
                    })}
                  >
                    {({ fieldProps, error }) => (
                      <div data-testid={`${fieldNamePrefix}offering`}>
                        <Select<OfferingOption>
                          {...fieldProps}
                          options={offeringOptions}
                          isSearchable={false}
                          onChange={(item: ValueType<OfferingOption, false>) => {
                            fieldProps.onChange(item);
                            onChange && onChange(fieldProps.name, item);
                          }}
                        />
                        {error && <ErrorMessage>Please select an offering</ErrorMessage>}
                      </div>
                    )}
                  </Field>
                </div>
              )}
              {isHiddenConfig?.skipTrial || (
                <CheckboxField
                  name={`${fieldNamePrefix}skipTrial`}
                  label="Skip trial"
                  defaultIsChecked={isSkipTrialChecked}
                  isDisabled={!!isDisabled?.skipTrial}
                >
                  {({ fieldProps }) => (
                    <Checkbox
                      {...fieldProps}
                      id={`${fieldNamePrefix}skipTrial`}
                      onChange={(e) => {
                        fieldProps.onChange(e);
                        onChange && onChange(fieldProps.name, e.target.checked);
                      }}
                      testId={`${fieldNamePrefix}skipTrial`}
                    />
                  )}
                </CheckboxField>
              )}
              {isHiddenConfig?.userTier || (
                <div className="field-width">
                  <Field<ValueType<UserTierOption>, HTMLSelectElement>
                    id={`${fieldNamePrefix}userTier`}
                    name={`${fieldNamePrefix}userTier`}
                    label={isJSMProduct(productId as string) ? 'Agents' : 'User tier'}
                    defaultValue={selectedUserTier}
                    isDisabled={!!isDisabled?.userTier}
                    {...(isRequiredConfig?.userTier && {
                      isRequired: true,
                      validate: (value: any) => (!value ? 'EMPTY' : undefined),
                    })}
                  >
                    {({ fieldProps, error }) => (
                      <div data-testid={`${fieldNamePrefix}userTier`}>
                        <Select
                          {...fieldProps}
                          options={userTierOptions}
                          isSearchable={false}
                          onChange={(item: ValueType<UserTierOption, false>) => {
                            fieldProps.onChange(item);
                            onChange && onChange(fieldProps.name, item);
                          }}
                        />
                        {error && <ErrorMessage>Please select a user tier</ErrorMessage>}
                      </div>
                    )}
                  </Field>
                </div>
              )}
              {isHiddenConfig?.units || (
                <div className="field-width">
                  <Field
                    id={`${fieldNamePrefix}units`}
                    label="Units"
                    name={`${fieldNamePrefix}units`}
                    isDisabled={!!isDisabled?.units}
                    defaultValue={selectedUnits}
                    isRequired={!!isRequiredConfig?.units}
                    validate={(value: string | undefined) =>
                      value && isInteger(+value) && +value >= 0 ? undefined : 'INVALID'
                    }
                  >
                    {({ fieldProps, error }) => (
                      <>
                        <Textfield
                          {...fieldProps}
                          onChange={(e) => {
                            fieldProps.onChange(e);
                            onChange && onChange(fieldProps.name, e.currentTarget.value);
                          }}
                          testId={`${fieldNamePrefix}units`}
                        />
                        {error && <ErrorMessage>Enter a valid number</ErrorMessage>}
                      </>
                    )}
                  </Field>
                </div>
              )}
            </div>
            {hasOptedForUpgradeTrial && (
              <span data-testid="upgrade-trial-banner" className="upgrade-trial-banner">
                <WarningIcon label="" primaryColor={Y200} />
                <p>Current plan will be charged during the no-cost trial period of Premium plan</p>
              </span>
            )}
            {isHiddenConfig?.pricingPlan || (
              <Field<ValueType<PricingPlanOption>, HTMLSelectElement>
                id={`${fieldNamePrefix}pricingPlan`}
                name={`${fieldNamePrefix}pricingPlan`}
                label="Pricing plan"
                defaultValue={selectedPricingPlan}
                isDisabled={!!isDisabled?.pricingPlan}
                {...(isRequiredConfig?.pricingPlan && {
                  isRequired: true,
                  validate: (value: any) => (!value ? 'EMPTY' : undefined),
                })}
              >
                {({ fieldProps, error }) => (
                  <div data-testid={`${fieldNamePrefix}pricingPlan`}>
                    <Select
                      {...fieldProps}
                      options={pricingPlanOptions}
                      isSearchable={false}
                      onChange={(item: ValueType<PricingPlanOption, false>) => {
                        fieldProps.onChange(item);
                        onChange && onChange(fieldProps.name, item);
                      }}
                    />
                    {error && <ErrorMessage>Please select a pricing plan</ErrorMessage>}
                  </div>
                )}
              </Field>
            )}
            {isHiddenConfig?.promotions || (
              <ManagePromotionsSection
                entitlement={entitlement}
                isHidden={isHiddenConfig}
                isLoading={isLoading}
                isDisabled={isDisabled}
                fieldName={fieldName}
                offeringPricingType={selectedOffering?.pricingType}
                billingFrequency={billingFrequency}
                availablePromotions={availablePromotions}
                partnerPromotion={partnerPromotion}
                selectedPromotionsToAdd={selectedPromotionsToAdd}
                selectedPromotionsToRemove={selectedPromotionsToRemove}
                selectedSchedulePromotionsToRemove={selectedSchedulePromotionsToRemove}
                onChange={onChange}
              />
            )}
          </div>
        </div>
      </CustomPanel>
    </div>
  );
};
